import createCrudService from '@src/core/api/create-crud-service';
import { QUERY_KEYS } from '@src/shared/constants/query-keys';
import type { TBaseQuery } from '@src/shared/types/base-query';
import { showToast } from '@src/shared/utils/helpers/show-toast';

// `apiClient`'s baseURL already ends in `/api/v1/crm/`, so every url here is
// relative to it (see apps/api/v1/knowledge_base/README.md on the server).

/** The compact agent row a base carries: who answers from it. */
export type KnowledgeBaseAgent = {
  id: number;
  name: string;
  role: string;
  tone: 'FRIENDLY' | 'PROFESSIONAL' | 'CONCISE';
};

export type KnowledgeBase = {
  id: number;
  name: string;
  description: string | null;
  is_active: boolean;
  document_count: number;
  faq_count: number;
  rich_text_count: number;
  website_count: number;
  ai_agents: KnowledgeBaseAgent[];
  created_at: string;
  updated_at: string;
};

export type KnowledgeBaseQuery = TBaseQuery & {
  q?: string;
  is_active?: boolean;
  /** true: at least one agent answers from the base; false: none does. */
  connected?: boolean;
};

export type KnowledgeBasePayload = {
  name: string;
  description: string | null;
  is_active?: boolean;
  ai_agent_ids?: number[];
};

const success = (title: string) => () =>
  showToast({ title, variant: 'success' });

export const knowledgeBaseApi = createCrudService<
  KnowledgeBase,
  KnowledgeBaseQuery,
  KnowledgeBasePayload
>({
  queryKey: [QUERY_KEYS.getKnowledgeBases],
  url: 'knowledge-bases',
  factory: {
    create: {
      tanstackOptions: { onSuccess: success('Knowledge base created') },
    },
    update: {
      tanstackOptions: { onSuccess: success('Knowledge base updated') },
    },
    delete: {
      tanstackOptions: { onSuccess: success('Knowledge base deleted') },
    },
  },
});
